import { useCallback, useEffect, useState } from 'react';
import { BackendAPI } from '../lib/api';
import { readReferralCookie } from '../lib/referral';

export interface UseCredits {
  balance: number | null;
  agreementAccepted: boolean;
  /** True while an unredeemed signup code exists (drives the inbox nudge). */
  signupCodePending: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * On mount (when `enabled`): idempotently bootstrap the account (records
 * signup meta + delivers the one free credit — instantly, or as an emailed
 * code when email infra is configured), persist agreement acceptance if the
 * signup checkbox consent isn't recorded yet, then read the balance.
 *
 * Pass `enabled=false` for member (role-holding) users — they're unmetered,
 * and bootstrapping them would pointlessly mint signup credits.
 */
export function useCredits(enabled: boolean = true): UseCredits {
  const [balance, setBalance] = useState<number | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [signupCodePending, setSignupCodePending] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const credits = await BackendAPI.getCredits();
    setBalance(credits.balance);
    setAgreementAccepted(credits.agreement_accepted);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // PRP-MCP-10: the ?ref= code, bound by the backend only on a brand-new account.
        const boot = await BackendAPI.meBootstrap(readReferralCookie());
        if (!boot.agreement_accepted) await BackendAPI.acceptAgreement();
        const credits = await BackendAPI.getCredits();
        if (cancelled) return;
        setBalance(credits.balance);
        setAgreementAccepted(credits.agreement_accepted);
        setSignupCodePending(!!boot.signup_code_pending);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your account.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  return { balance, agreementAccepted, signupCodePending, loading, error, refresh };
}
